import { Box, Flex, Grid, Heading, Image, Text } from '@chakra-ui/react';
import { BigNumber, ethers } from 'ethers';
import { FC, useState } from 'react';
import { GamePanelItem } from 'src/pages/Games/games';
import { GameMini } from '../GameMini/gameMini';
import { HashupGameUploader } from '../../../lib/HashupFileUploader/HashupFileUploader';
import { DetailsButton } from './DetailsButton/detailsButton';
import { GameStatus } from './GameStatus/gameStatus';

import { PanelNavigation } from './PanelNavigation/panelNavigation';
import { ChartsTab } from './ChartsTab/chartsTab';
import { PlatformIconAssets } from 'src/config';
import { ManageTab } from './ManageTab/manageTab';
import { Link } from 'react-router-dom';
import { MetadataTab } from './MetadataTab/metadataTab';
import { Formik } from 'formik';
import { getMetadataEditSchema } from './MetadataTab/schema';
import { MigrationTab } from './MigrationTab/migrationTab';
import { Color } from 'src/theme/color';
import { TextSection } from './TextSection/textSection';
import { useTranslation } from 'react-i18next';
import { CodeTab } from './CodeTab/codeTab';

interface IGamePanel {
	game: GamePanelItem;
}

export enum GamePanelPages {
	CHART,
	PRICING,
	UPLOAD,
	METADATA,
	MIGRATION,
	CODE,
}

export const GamePanel: FC<IGamePanel> = ({ game }) => {
	const [selectedPage, selectPage] = useState<GamePanelPages>(GamePanelPages.CHART);
	const [detailsOpened, setDetailsOpened] = useState<boolean>(false);

	const { t } = useTranslation();

	const currentPage = {
		[GamePanelPages.CHART]: () => (
			<ChartsTab
				address={game.cartridgeAddress}
				creator={game.creator}
			/>
		),
		[GamePanelPages.PRICING]: () => <ManageTab address={game.cartridgeAddress} />,
		[GamePanelPages.UPLOAD]: () => (
			<Box>
				{game.platforms.includes('pc') && (
					<Flex gap="40px">
						<Box
							border="2px solid"
							borderRadius="4px"
							bg={Color.BLACK_4}
							borderColor={Color.HASHUP}
						>
							<HashupGameUploader
								appName={game.name ?? ''}
								appCatalogAppId={game.patchkitAppId ?? ''}
								endpoint={process.env.REACT_APP_UPLOAD_ENDPOINT ?? ''}
								tokenId={game.cartridgeAddress ?? ''}
								iconUrl={game.logoUrl ?? ''}
								fileUploaded={async (e: any) => {
									// `/metadata?appCatalogAppId=${e.detail.app_catalog_app_id}&tokenId=${game.cartridgeAddress}&iconUrl=${game.logoUrl}`,
								}}
							/>
						</Box>
						<TextSection heading={t('yourGames.upload.heading')}>
							{t('yourGames.upload.description')}
						</TextSection>
					</Flex>
				)}
				{game.platforms.includes('html') && (
					<Link to="https://github.com/hashup-it/hashup-plugin"> {t('yourGames.upload.html')} </Link>
				)}
			</Box>
		),
		[GamePanelPages.METADATA]: () => (
			<Box>
				<Formik
					initialValues={{
						gameTitle: game.name,
						abbreviation: game.symbol,
						gameUrl: 'https://www.google.com/',
						description: '',
						media: {
							coverImageUrl:
								'https://static.vecteezy.com/packs/media/components/global/search-explore-nav/img/vectors/term-bg-1-666de2d941529c25aa511dc18d727160.jpg', //
							logoUrl:
								'https://static.vecteezy.com/packs/media/components/global/search-explore-nav/img/vectors/term-bg-1-666de2d941529c25aa511dc18d727160.jpg', ///
							banner: 'https://static.vecteezy.com/packs/media/components/global/search-explore-nav/img/vectors/term-bg-1-666de2d941529c25aa511dc18d727160.jpg', /// /
						},
						socials: {
							facebook: 'https://www.google.com/',
							twitter: 'https://www.google.com/',
							youtube: 'https://www.google.com/',
							discord: 'https://www.google.com/',
						},
						colors: game.colors,
						screenshots: game.screenshots,
						genre: game.genres,
						video: 'https://www.youtube.com/watch?v=jRjFn__kVJM',
						gamepaper: 'https://hashup.it/',
					}}
					onSubmit={() => {}}
					validationSchema={getMetadataEditSchema()}
				>
					<MetadataTab address={game.cartridgeAddress} />
				</Formik>
			</Box>
		),
		[GamePanelPages.MIGRATION]: () => (
			<MigrationTab
				isInStore={game.marketplaceFee != ''}
				address={game.cartridgeAddress}
			/>
		),
		[GamePanelPages.CODE]: () => <CodeTab cartridge={game.cartridgeAddress} />,
	};

	return (
		<Box
			bg={detailsOpened ? Color.FORM_BACKGROUND : '#131313'}
			padding="42px 50px"
			borderBottom={detailsOpened ? '1px solid #333' : 'none'}
		>
			<Grid
				templateColumns="300px 140px 100px auto 100px"
				gridGap="20px"
				justifyItems="start"
				alignItems="center"
			>
				<GameMini
					title={game.name ?? ''}
					abbreviation={game.symbol ?? ''}
					color={game.color ?? 'gold'}
					address={game.cartridgeAddress}
					logoUrl={game.logoUrl ?? ''}
				/>
				<DetailsButton
					open={() => setDetailsOpened(!detailsOpened)}
					opened={detailsOpened}
				/>
				<Text
					fontWeight="bold"
					fontSize="14px"
				>
					${ethers.utils.formatUnits(BigNumber.from(game.price ?? '10').mul(100), 6)}
				</Text>
				<Flex
					justifyItems="center"
					onClick={() => (game.listed = true)}
					gap="12px"
					textTransform="uppercase"
					fontWeight={'bold'}
				>
					{game.platforms
						? game.platforms.map(item => {
								switch (item) {
									case 'pc':
										return (
											<Image
												key={item}
												src={PlatformIconAssets.pc}
											/>
										);
									case 'html':
										return (
											<Image
												key={item}
												src={PlatformIconAssets.html}
											/>
										);
									case 'software-key':
										return <Box fontSize="12px">Key</Box>;
									default:
										return <Box fontSize="12px">Web3</Box>;
								}
						  })
						: ''}
				</Flex>
				<GameStatus listed={game.listed} />
			</Grid>
			{detailsOpened && (
				<Box>
					<PanelNavigation
						platforms={game.platforms}
						selectedPage={selectedPage}
						selectPage={selectPage}
					/>
					{currentPage[selectedPage]()}
				</Box>
			)}
		</Box>
	);
};
